import { isArray, isDate } from "lodash";
import { FC, useCallback } from "react";
import { Controller, type Control } from "react-hook-form";
import styled from "styled-components";

import { Calendar, type CalendarChangeEvent } from "@ds/components/calendars";
import { TextInputMixin } from "@ds/components/forms/inputs/styles";
import { LabelWrapper } from "@ds/components/forms/labels";

const formatNameToLabel = (name: "start_at" | "end_at") => {
  return name
    .split("_")
    .map((word, i) => (i === 0 ? word.charAt(0).toUpperCase() + word.slice(1) : "Date"))
    .join(" ");
};

const formatPlaceholderText = (inputId: string) => {
  return inputId === "start_at" ? "Once deployed" : "Never expires";
};

const CalendarInputWrapper = styled.div`
  width: 100%;

  ${TextInputMixin}

  ::placeholder,
  .p-inputtext::placeholder {
    color: var(--textColor60);
  }

  .p-calendar {
    width: 100%;
  }

  .p-button {
    background: var(--primaryColor090);
    color: var(--textColor060);
    border: none;
    border-radius: 0;
    padding: 0;
    margin: 0;
    font-size: 0.9rem;
    font-weight: 500;

    &:enabled:hover {
      background: var(--primaryColor100);
    }

    &:focus {
      box-shadow: var(--button-light-box-shadow);
    }

    svg {
      stroke: var(--white-color);
    }
  }
`;

export const CalendarInput: FC<{
  onChange: (date: Date | null) => void;
  value?: Date;
  inputId: string;
  placeholder?: string;
}> = ({ onChange, value, inputId, placeholder }) => {
  const onDateChangeHandler = useCallback(
    ({ value: calendarValue }: CalendarChangeEvent) => {
      if (calendarValue && !isArray(calendarValue) && isDate(calendarValue)) {
        onChange(calendarValue);
      } else if (!calendarValue) {
        onChange(null);
      }
    },
    [onChange],
  );

  /**
   * @description: This is a workaround to fix the issue with the calendar not being focused when the input is clicked.
   * @see: https://github.com/primefaces/primereact/issues/4568#issuecomment-1709983831
   */
  const handleCalendarFocus = () => {
    setTimeout(() => {
      const datePickerCollection = document.getElementsByClassName("p-datepicker");
      if (datePickerCollection.length) {
        const datePicker = datePickerCollection.item(0) as HTMLDivElement;
        datePicker.setAttribute("tabindex", "0");
        datePicker.focus();
      }
    }, 500);
  };

  return (
    <CalendarInputWrapper>
      <Calendar
        id={inputId}
        value={value || null}
        onChange={onDateChangeHandler}
        hourFormat="12"
        showIcon
        showTime
        showButtonBar
        placeholder={placeholder ?? formatPlaceholderText(inputId)}
        onFocus={handleCalendarFocus}
      />
    </CalendarInputWrapper>
  );
};

export type PlaylistAssetDateControlProps = {
  control: Control<PlaylistAsset>;
  name: "start_at" | "end_at";
  placeholder?: string;
};

export const PlaylistAssetDateControl: FC<PlaylistAssetDateControlProps> = ({ control, name, placeholder }) => {
  const label = formatNameToLabel(name);
  return (
    <Controller
      name={name}
      control={control}
      render={({ field: { value, onChange }, fieldState }) => {
        return (
          <>
            <LabelWrapper className="col-8" label={label} error={fieldState.error} htmlFor={name}>
              <CalendarInput
                onChange={onChange}
                value={value ? new Date(value) : undefined}
                inputId={name}
                placeholder={placeholder}
              />
            </LabelWrapper>
          </>
        );
      }}
    />
  );
};
